import { z } from 'zod';
import { blockSchema } from './schemas.js';

// =====================================================================
// Section + column layout shape.
// =====================================================================
//
// A page is composed of sections stacked vertically. Each section has
// a fixed column split (chosen at section-creation time, immutable) and
// holds one block list per column.
//
// Split values use compact ratios:
//   'full'   → 1 column (the entire row)
//   '2-1'    → 2 columns: 2/3 + 1/3
//   '1-2'    → 2 columns: 1/3 + 2/3
//   '1-1'    → 2 columns: 1/2 + 1/2
//   '1-1-1'  → 3 columns: 1/3 + 1/3 + 1/3
//
// Mobile (<768px) always collapses every multi-column section to a
// single column in source order. Renderer + editor honor this rule.

export const sectionSplitSchema = z.enum(['full', '2-1', '1-2', '1-1', '1-1-1']);
export type SectionSplit = z.infer<typeof sectionSplitSchema>;

export const SECTION_SPLIT_COLUMN_COUNT: Record<SectionSplit, number> = {
  full: 1,
  '2-1': 2,
  '1-2': 2,
  '1-1': 2,
  '1-1-1': 3,
};

// Structural caps so a write can't store an unbounded / arbitrarily-deep page
// (stored DoS, D1 bloat, slow renders). Generous vs. any real SMB page — a real
// page has a handful of sections and a few blocks per column — but a hard
// ceiling at the Zod write boundary (CLAUDE.md: every JSON column parses on
// write). A section never has more than 3 columns (the widest split, '1-1-1').
export const MAX_SECTIONS_PER_PAGE = 80;
export const MAX_COLUMNS_PER_SECTION = 3;
export const MAX_BLOCKS_PER_COLUMN = 60;

export const sectionSchema = z
  .object({
    id: z.string(),
    // Optional template slot key for content mapping during layout
    // switching (e.g. 'hero', 'main-services', 'sidebar-contact').
    slotKey: z.string().optional(),
    split: sectionSplitSchema,
    // Outer array = columns (length matches the split's column count).
    // Inner arrays = blocks within each column.
    columns: z.array(z.array(blockSchema).max(MAX_BLOCKS_PER_COLUMN)).max(MAX_COLUMNS_PER_SECTION),
  })
  // The column count MUST match the split, or the renderer mis-maps columns
  // (and blocks in an overflow column are silently dropped). Enforce it at the
  // write boundary rather than trusting the editor.
  .superRefine((s, ctx) => {
    const expected = SECTION_SPLIT_COLUMN_COUNT[s.split];
    if (s.columns.length !== expected) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['columns'],
        message: `split '${s.split}' requires ${expected} column(s), got ${s.columns.length}`,
      });
    }
  });
export type Section = z.infer<typeof sectionSchema>;

// =====================================================================
// Page content v2 wrapper.
// =====================================================================
//
// On disk, `pages.blocks_json` can be either:
//   v1: `Block[]` (legacy flat array — Phase 3B-3c and earlier)
//   v2: `PageContentV2` (this object — Phase 3B-3.5+)
//
// The discriminator is structural: `Array.isArray(raw)` → v1. The
// schema_version column on `pages` is the source of truth, but we
// don't trust it — we also check shape, because legacy rows may have
// schema_version=1 with v2 JSON (and vice versa) after operator
// migrations. See `normalizePageContent` in normalize.ts.

export const pageContentV2Schema = z.object({
  version: z.literal(2),
  // Identifies the original template the page was built from. Used
  // for "Change layout" diffing. Optional because "Start blank" and
  // legacy-stack-migrated pages don't carry one (or carry the
  // internal 'legacy-stack' sentinel).
  layoutTemplate: z.string().optional(),
  sections: z.array(sectionSchema).max(MAX_SECTIONS_PER_PAGE),
});
export type PageContentV2 = z.infer<typeof pageContentV2Schema>;
